import { defineStore } from 'pinia'
import {
  getChecksByLedger,
  toggleChecks as toggleChecksApi,
  getUnreadChecksCount,
} from '../api'
import { SfLog } from '@app/adapters/logger'
import { computed, ref, ComputedRef } from 'vue'
import type { Check, Workflow, CheckGroup } from '../types'
import { getWorkflows } from '@app/application/communication/screens/inbox/api/workflows'
import {
  WorkflowListItem,
  CheckPayload,
  CheckContext,
} from '@app/shared/types/sf-data'

import {
  groupContextsById,
  getFilteredContextIds,
  getChecksIdsByGroupId,
  getChecksWithStatusById,
} from '../utils'

export const useSfAssistantChecksPage = defineStore(
  'sfAssistantChecksPage',
  () => {
    const checksById = ref<Record<string, Check>>({})
    const generalCheckIds = ref<string[]>([])
    const contextById = ref<Record<string, CheckContext>>({})
    const workflowById = ref<Record<number, Workflow>>({})
    const loading = ref(false)
    const unreadCount = ref<number | null>(null)

    const groupedCheckIds = ref<
      Record<
        string,
        {
          [key: string]: string[]
        }
      >
    >({})
    const groupedContextIds = ref<Record<number, number[]>>({})
    const workflowIds = ref<number[]>([])
    const workflowId = ref<number | null>(null)

    const checkGroups: ComputedRef<CheckGroup[]> = computed(() => {
      if (workflowId.value) {
        return ['accounts', 'reconciliations']
      }

      return ['accounts', 'reconciliations', 'general']
    })

    const receiveChecks = async (): Promise<CheckPayload | null> => {
      try {
        const options = {
          skip_platform_checks: true,
          ...(workflowId.value ? { workflow_id: workflowId.value } : {}),
        }

        return await getChecksByLedger(options)
      } catch (error) {
        SfLog.warn('Page -> receiveChecks failed', {
          extra: { error, ...window.Silverfin.jsonParams },
        })
        throw error
      }
    }

    const receiveWorkflows = async (): Promise<WorkflowListItem[] | null> => {
      try {
        return await getWorkflows({
          ledgerId: window.Silverfin.currentLedgerId(),
          company_id: window.Silverfin.currentCompanyId,
        })
      } catch (error) {
        SfLog.warn('Failed to load company workflows', {
          extra: {
            ...window.Silverfin.jsonParams,
            error,
          },
        })
        throw error
      }
    }

    const setWorkflowId = (context: number): void => {
      workflowId.value = context
    }

    const fetchAll = async (): Promise<void> => {
      loading.value = true

      try {
        const [workflows, checks] = await Promise.all([
          receiveWorkflows(),
          receiveChecks(),
        ])

        if (workflows !== null) {
          processWorkflows(workflows)
        }

        if (workflows !== null && checks !== null) {
          processChecks(checks)
        }
      } catch (error) {
        SfLog.warn('Failed to load workflows/checks', {
          extra: {
            ...window.Silverfin.jsonParams,
            error,
          },
        })
      } finally {
        loading.value = false
      }
    }

    const fetchUnreadCount = async (): Promise<void> => {
      try {
        const data = await getUnreadChecksCount(workflowId.value)
        if (data) {
          unreadCount.value = data.count
        }
      } catch (error) {
        SfLog.warn('Failed to load unread checks count', {
          extra: {
            ...window.Silverfin.jsonParams,
            error,
          },
        })
      }
    }

    const toggleCheck = async (checkId: string): Promise<void> => {
      // Update the read status of the check/counter immediately
      // so we do not wait for the loading to finish
      const initialCheckReadStatus = checksById.value[checkId].read
      checksById.value[checkId] = {
        ...checksById.value[checkId],
        read: !initialCheckReadStatus,
      }
      unreadCount.value = initialCheckReadStatus
        ? totalUnreadChecks.value + 1
        : totalUnreadChecks.value - 1

      try {
        await toggleChecksApi({
          type: initialCheckReadStatus ? 'unread' : 'read',
          checkIds: [checkId],
        })
      } catch (error) {
        // Revert the read status of the check/counter immediately
        // in case of an error
        checksById.value[checkId] = {
          ...checksById.value[checkId],
          read: initialCheckReadStatus,
        }
        unreadCount.value = initialCheckReadStatus
          ? totalUnreadChecks.value - 1
          : totalUnreadChecks.value + 1
        SfLog.warn('Failed to execute toggleCheck()', {
          extra: { error, ...window.Silverfin.jsonParams },
        })
      }

      await fetchUnreadCount()
    }

    const markChecksAsRead = async (checkIds: string[]): Promise<void> => {
      // Update the read status of the checks/counter immediately
      // so we do not wait for the loading to finish
      const initialChecksById = checksById.value
      checkIds.forEach((checkId) => {
        checksById.value[checkId] = {
          ...checksById.value[checkId],
          read: true,
        }
      })
      const initialTotalUnreadChecks = totalUnreadChecks.value
      unreadCount.value = initialTotalUnreadChecks - checkIds.length

      try {
        await toggleChecksApi({
          type: 'read',
          checkIds,
        })
      } catch (error) {
        // Revert the read status of the checks/counter immediately
        // in case of an error
        checksById.value = initialChecksById
        unreadCount.value = initialTotalUnreadChecks
        SfLog.warn('Failed to execute markChecksAsRead()', {
          extra: { error, ...window.Silverfin.jsonParams },
        })
        throw error
      }
    }

    const processWorkflows = (workflowsResponse: WorkflowListItem[]): void => {
      // If there is a specific workflowId selected, filter the workflows
      const workflows = workflowId.value
        ? workflowsResponse.filter(
            (wf: WorkflowListItem): boolean => wf.id === workflowId.value
          )
        : workflowsResponse

      // sorted list of workflow ids for rendering
      workflowIds.value = workflows
        .sort((a, b) => {
          if (a.default) {
            return -1
          } else if (b.default) {
            return 1
          }
          return a.name.localeCompare(b.name)
        })
        .map((wf) => wf.id)

      workflowById.value = workflows.reduce(
        (acc, wf) => ({
          ...acc,
          [wf.id]: {
            ...wf,
            expanded: true,
          },
        }),
        {}
      )
    }

    const processChecks = (checksResponse: CheckPayload): void => {
      const generalChecksById: Record<string, Check> = {}
      let checkIdsByAccountId: Record<number, string[]> = {}
      let checkIdsByReconciliationsId: Record<number, string[]> = {}

      contextById.value = groupContextsById(checksResponse.contexts)

      checksById.value = checkGroups.value.reduce((acc, groupName) => {
        if (groupName === 'general') {
          generalCheckIds.value = checksResponse[groupName].map((check) => {
            generalChecksById[check.id] = {
              ...check,
              read: checksResponse.readStatus.includes(check.id),
              contextUrl: '',
              contextUrlText: '',
            }
            return check.id
          })

          return { ...acc, ...generalChecksById }
        }

        const checksIdsByGroupId = getChecksIdsByGroupId(
          checksResponse[groupName]
        )

        if (groupName === 'accounts') {
          checkIdsByAccountId = checksIdsByGroupId
        } else if (groupName === 'reconciliations') {
          checkIdsByReconciliationsId = checksIdsByGroupId
        }

        const checksWithStatusById = getChecksWithStatusById(
          checksResponse[groupName],
          checksResponse.readStatus
        )

        return {
          ...acc,
          ...checksWithStatusById,
        }
      }, {})

      groupedCheckIds.value = workflowIds.value.reduce(
        (acc, workflowId) => ({
          ...acc,
          [workflowId]: getFilteredContextIds(
            contextById.value,
            workflowById.value[workflowId]
          ).reduce((acc, contextId) => {
            const effectiveContextId = parseInt(contextId.split('__')[1], 10)
            let checkIds: string[] = []
            const contextType = contextById.value[contextId].type

            if (contextType === 'Account') {
              checkIds = checkIdsByAccountId[effectiveContextId]
            } else if (contextType === 'ReconciliationText') {
              checkIds = checkIdsByReconciliationsId[effectiveContextId]
            }

            // This is for updating checks with proper context
            checkIds.forEach((checkId) => {
              checksById.value[checkId] = {
                ...checksById.value[checkId],
                contextUrl: contextById.value[contextId].url,
                contextUrlText: contextById.value[contextId].name,
              }
            })

            return !checkIds.length
              ? acc
              : {
                  ...acc,
                  [contextId]: checkIds,
                }
          }, {}),
        }),
        {}
      )

      groupedContextIds.value = Object.keys(groupedCheckIds.value).reduce(
        (acc, workflowId) => {
          const effectiveWorkflowId = parseInt(workflowId, 10)
          return {
            ...acc,
            [effectiveWorkflowId]: Object.keys(
              groupedCheckIds.value[effectiveWorkflowId]
            ).sort((a, b) =>
              contextById.value[a].name.localeCompare(contextById.value[b].name)
            ),
          }
        },
        {}
      )
    }

    const groupedUnreadCheckIds: ComputedRef<{
      [key: string]: {
        [key: string]: string[]
      }
    }> = computed(() => {
      return Object.keys(groupedCheckIds.value).reduce((acc, workflowById) => {
        const effectiveWorkflowById = parseInt(workflowById, 10)
        return {
          ...acc,
          [effectiveWorkflowById]: Object.keys(
            groupedCheckIds.value[effectiveWorkflowById]
          ).reduce(
            (acc, contextId) => ({
              ...acc,
              [contextId]: groupedCheckIds.value[effectiveWorkflowById][
                contextId
              ].filter((checkId) => !checksById.value[checkId].read),
            }),
            {}
          ),
        }
      }, {})
    })

    const groupedUnreadCheckIdsByWorkflow: ComputedRef<{
      [key: string]: string[]
    }> = computed(() =>
      Object.keys(groupedUnreadCheckIds.value).reduce(
        (acc, workflowId) => ({
          ...acc,
          [workflowId]: Object.keys(
            groupedUnreadCheckIds.value[workflowId]
          ).reduce(
            (acc: string[], contextId) => [
              ...new Set(
                acc.concat(groupedUnreadCheckIds.value[workflowId][contextId])
              ),
            ],
            []
          ),
        }),
        {}
      )
    )

    const generalUnreadCheckIds: ComputedRef<string[]> = computed(() =>
      generalCheckIds.value.filter((checkId) => !checksById.value[checkId].read)
    )

    const hasChecks: ComputedRef<boolean> = computed(
      () => Object.keys(checksById.value).length > 0
    )

    const unreadCheckIds: ComputedRef<string[]> = computed(() => {
      const totalGroupedUnreadCheckIds = (
        Object.keys(groupedUnreadCheckIdsByWorkflow.value) || []
      ).reduce(
        (acc: string[], workflowId) =>
          acc.concat(groupedUnreadCheckIdsByWorkflow.value[workflowId]),
        []
      )
      return [
        ...new Set(
          totalGroupedUnreadCheckIds.concat(generalUnreadCheckIds.value)
        ),
      ]
    })

    // Total unread checks count that can be calculated by
    // prior calling of either:
    // receiveChecks() OR fetchUnreadCount() OR both
    // Even if both are called the faster response wins here.
    const totalUnreadChecks = computed(() => {
      // If unread-count is already set by a seperate
      // fetchUnreadCount() call then return that instead
      if (unreadCount.value) {
        return unreadCount.value
      }

      // otherwise return the length of the unread checks
      // fetched by receiveChecks()
      return unreadCheckIds.value.length
    })

    return {
      groupedUnreadCheckIdsByWorkflow,
      hasChecks,
      unreadCheckIds,
      totalUnreadChecks,
      generalUnreadCheckIds,
      groupedUnreadCheckIds,
      groupedContextIds,
      groupedCheckIds,
      contextById,
      workflowIds,
      workflowById,
      checksById,
      generalCheckIds,
      loading,
      fetchAll,
      fetchUnreadCount,
      toggleCheck,
      markChecksAsRead,
      setWorkflowId,
    }
  }
)
